import { type FC } from "react";
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@/shared/components";
import { touchSummary } from "@/shared/components/AttributionTouch";
import { interpolationFormat } from "@/partner/shared/lib";
import {
  AssistantMessageKindType,
  AssistantMessageRoleType,
} from "@/types/graphql/partner.generated";
import { useAssistantConversationQuery } from "@/partner/entities/assistant";

interface ConversationDialogProps {
  open: boolean;
  conversationId: string | null;
  onClose: () => void;
}

// Текст сообщений kind != TEXT: у OFF_TOPIC/BLOCKED/ERROR поле text либо
// пустое, либо служебное, партнёру нужна человекочитаемая причина.
const KIND_TEXT_KEY: Record<AssistantMessageKindType, string | null> = {
  [AssistantMessageKindType.TextType]: null,
  [AssistantMessageKindType.OffTopicType]: "conversations.kindOffTopic",
  [AssistantMessageKindType.BlockedType]: "conversations.kindBlocked",
  [AssistantMessageKindType.ErrorType]: "conversations.kindError",
  // Итог плана бывает только в диалогах сотрудника с помощником партнёра,
  // а здесь диалоги зрителей (audience != PARTNER).
  [AssistantMessageKindType.PlanResultType]: null,
};

export const ConversationDialog: FC<ConversationDialogProps> = ({
  open,
  conversationId,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, loading, error } = useAssistantConversationQuery({
    variables: { id: conversationId ?? "" },
    skip: !conversationId,
    fetchPolicy: "cache-and-network",
  });

  const conversation = data?.assistantConversation;
  const emptyValue = t("common.emptyDash");

  const formatDateTime = (value: string): string =>
    interpolationFormat(value, "dateTime", i18n.language);

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>
            {conversation?.title || t("conversations.dialogTitle")}
          </Typography>
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 1 }}>
        {error && <ErrorMessage error={error} />}

        {loading && !conversation && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {conversation && (
          <>
            <Box sx={{ p: 1, display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("conversations.viewerLabel")}
                </Typography>
                <Typography variant="body2">
                  {conversation.user?.email ?? emptyValue}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("conversations.startedLabel")}
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(conversation.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("conversations.cameLabel")}
                </Typography>
                <Typography variant="body2">
                  {touchSummary(conversation.utm)}
                </Typography>
              </Box>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 160 }}>
                    {t("conversations.lastMessageColumn")}
                  </TableCell>
                  <TableCell sx={{ width: 140 }} />
                  <TableCell>{t("conversations.messagesColumn")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conversation.messages.map((message) => {
                  const kindTextKey = KIND_TEXT_KEY[message.kind];
                  const isUser =
                    message.role === AssistantMessageRoleType.UserType;

                  return (
                    <TableRow key={message.id}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(message.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {isUser
                            ? t("conversations.roleUser")
                            : t("conversations.roleAssistant")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {kindTextKey ? (
                          <Typography variant="body2" fontStyle="italic">
                            {t(kindTextKey)}
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {message.text}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
